/**
 * Shared pieces for light in the lobby. There is no bloom on the Quest, so glow is drawn:
 * additive, unlit, untonemapped materials and soft radial sprites.
 */
import * as THREE from 'three';

/** Additive, unlit material that ignores depth writes and fog: the basis of every glow. */
export function glowMaterial(
  colour: THREE.ColorRepresentation,
  opacity = 1,
  map?: THREE.Texture,
): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({
    color: colour,
    map: map ?? null,
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
    fog: false,
    side: THREE.DoubleSide,
    // Additive or see-through: one pass, not back faces then front faces.
    forceSinglePass: true,
  });
}

let radial: THREE.CanvasTexture | undefined;

/** A white radial falloff (shared, made once): floor pools, halos, soft points. */
export function radialTexture(): THREE.CanvasTexture {
  if (radial) return radial;
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.14)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  radial = new THREE.CanvasTexture(canvas);
  radial.colorSpace = THREE.SRGBColorSpace;
  return radial;
}

/** Deterministic 0..1 sequence so the hall is the same for everyone. */
export function seeded(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

/** Shared time uniform: every animated shader in the lobby reads this one value. */
export interface TimeUniform {
  value: number;
}
